import type { FixtureElement } from "./types";

export interface InspectionHierarchyNode {
  readonly id: string;
  readonly label: string;
  readonly kind: "dataset" | "scope" | "type";
  readonly records: number;
  readonly renderable: number;
  readonly editable: number;
  readonly elementIds: readonly string[];
  readonly children: readonly InspectionHierarchyNode[];
}

interface MutableHierarchyNode {
  readonly id: string;
  readonly label: string;
  readonly kind: "dataset" | "scope" | "type";
  readonly elements: FixtureElement[];
  readonly children: Map<string, MutableHierarchyNode>;
}

function floorScope(element: FixtureElement): { readonly id: string; readonly label: string } | null {
  const match = /^ifc-floor-(\d+)(?:-|$)/.exec(element.id);
  if (match === null) return null;
  const floor = Number(match[1]);
  return { id: `floor-${String(floor).padStart(8, "0")}`, label: `Floor ${floor}` };
}

function scopeFor(element: FixtureElement): { readonly id: string; readonly label: string } {
  const floor = floorScope(element);
  if (floor !== null) return floor;
  if (element.type === "Building") return { id: "building", label: "Buildings" };
  if (element.type === "Skipped") return { id: "producer-skipped", label: "Producer skipped evidence" };
  if (element.id.startsWith("ifc-plan-systems-")) return { id: "systems", label: "Systems" };
  if (element.id.startsWith("ifc-plan-site-")) return { id: "site", label: "Site" };
  if (element.id.startsWith("ifc-plan-")) return { id: "plan-wide", label: "Plan-wide evidence" };
  return { id: "dataset-records", label: "Dataset records" };
}

function freezeNode(node: MutableHierarchyNode): InspectionHierarchyNode {
  const children = [...node.children.values()]
    .sort((left, right) => left.id.localeCompare(right.id))
    .map(freezeNode);
  const elementIds = node.elements.map((element) => element.id).sort((left, right) => left.localeCompare(right));
  return {
    id: node.id,
    label: node.label,
    kind: node.kind,
    records: elementIds.length,
    renderable: node.elements.filter((element) => element.geometry !== null).length,
    editable: node.elements.filter((element) => element.editable && element.unsupportedReason === undefined).length,
    elementIds,
    children
  };
}

export function buildInspectionHierarchy(
  datasetId: string,
  datasetLabel: string,
  elements: readonly FixtureElement[]
): InspectionHierarchyNode {
  const uniqueIds = new Set(elements.map((element) => element.id));
  if (uniqueIds.size !== elements.length) throw new RangeError("Inspection hierarchy requires unique element ids.");
  const root: MutableHierarchyNode = {
    id: `dataset:${datasetId}`,
    label: datasetLabel,
    kind: "dataset",
    elements: [...elements],
    children: new Map()
  };
  for (const element of elements) {
    const scope = scopeFor(element);
    let scopeNode = root.children.get(scope.id);
    if (scopeNode === undefined) {
      scopeNode = { id: `scope:${scope.id}`, label: scope.label, kind: "scope", elements: [], children: new Map() };
      root.children.set(scope.id, scopeNode);
    }
    scopeNode.elements.push(element);
    let typeNode = scopeNode.children.get(element.type);
    if (typeNode === undefined) {
      // element.type can be "" (see collectionType() in local-ifc-projection.ts, which is not
      // routed through a non-empty fallback like almost every other field this importer produces).
      // An empty label previously rendered as a blank tree row indistinguishable from a layout bug
      // (kaz, 2026-08-11) — the map key stays the raw type so records still group correctly.
      const typeLabel = element.type === "" ? "(missing type)" : element.type;
      typeNode = {
        id: `${scopeNode.id}:type:${element.type}`,
        label: typeLabel,
        kind: "type",
        elements: [],
        children: new Map()
      };
      scopeNode.children.set(element.type, typeNode);
    }
    typeNode.elements.push(element);
  }
  return freezeNode(root);
}
